import React, { useMemo } from 'react';
import clsx from 'clsx';

import { DivPropsWithoutRef, getReactChildrenText, IntrinsicPropsWithoutRef, renderSlot, Slot } from '../../common';

export interface ListItemSlotProps {
  className?: string;
  hovered?: boolean;
}

export interface DropdownMenuBaseItemProps<
  Left extends { [key: string]: any } = { [key: string]: any },
  Right extends { [key: string]: any } = { [key: string]: any }
> extends DivPropsWithoutRef {
  /**
   * Проп для контролируемого включения состояния неактивности
   */
  disabled?: boolean;
  /**
   * Проп для контролируемого включения состояния выбранности
   */
  selected?: boolean;
  /**
   * Проп для контролируемого включения состояния ховера
   */
  hovered?: boolean;
  /**
   * Проп для контролируемого включения состояния фокуса
   */
  focused?: boolean;
  /**
   * Слот для левой части
   */
  left?: Slot<ListItemSlotProps>;
  /**
   * Слот для правой части
   */
  right?: Slot<ListItemSlotProps>;
  /**
   * Компонент для левой части.
   * Если передан, то используется вместо left
   */
  leftComponent?: React.ElementType<ListItemSlotProps & Left>;
  /**
   * Дополнительные пропы компонента для левой части
   */
  leftComponentProps?: Left;
  /**
   * Компонент для правой части.
   * Если передан, то используется вместо right
   */
  rightComponent?: React.ElementType<ListItemSlotProps & Right>;
  /**
   * Дополнительные пропы компоненты для правой части
   */
  rightComponentProps?: Right;
  /**
   * Максимальное количество строк текста для отображения
   */
  lineClamp?: number | false;
}

export const DropdownMenuBaseItem = React.forwardRef<
  HTMLDivElement,
  DropdownMenuBaseItemProps
>(
  (
    {
      left,
      leftComponentProps,
      leftComponent,
      right,
      rightComponentProps,
      rightComponent,
      children,
      className,
      selected,
      disabled,
      hovered,
      focused,
      lineClamp,
      style,
      ...props
    },
    ref
  ) => {
    const leftSlot = renderSlot(left, leftComponentProps, leftComponent);
    const rightSlot = renderSlot(right, rightComponentProps, rightComponent);
    const rootStyles: React.CSSProperties = useMemo(
      () => ({
        ...style,
        ...(lineClamp
          ? {
              '--DropdownMenuBaseItem-line-clamp': lineClamp
            }
          : {})
      }),
      [style, lineClamp]
    );
    const title = useMemo(
      () =>
        props.title ?? (lineClamp ? getReactChildrenText(children) : undefined),
      [props.title, lineClamp, children]
    );
    return (
      <div
        ref={ref}
        className={clsx('hse-DropdownMenuBaseItem', className, {
          'hse-DropdownMenuBaseItem_selected': selected,
          'hse-DropdownMenuBaseItem_disabled': disabled,
          'hse-DropdownMenuBaseItem_hovered': hovered,
          'hse-DropdownMenuBaseItem_focused': focused
        })}
        style={rootStyles}
        title={title}
        {...props}
      >
        {leftSlot && (
          <div className="hse-DropdownMenuBaseItem__left">{leftSlot}</div>
        )}
        <div className="hse-DropdownMenuBaseItem__content">{children}</div>
        {rightSlot && (
          <div className="hse-DropdownMenuBaseItem__right">{rightSlot}</div>
        )}
      </div>
    );
  }
);
